import React, { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@mui/material";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";

import { useNotify } from "hooks";
import { QUERY_KEYS } from "consts";
import { checkNullValue } from "utils";
import { addRecDataBySite, editRecDataBySite } from "api/rec";
import { RecModalProps, FormValues } from "./types";
import { CUSTOM_FIELDS_ORDER } from "./consts";
import { RenderField } from "./components";


export const RecModal: FC<RecModalProps> = ({ isOpen, onClose, allowedValues, columns, site, editREC }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state } = useLocation();

  const recData = state?.collector;

  const { countries = [], utilityTypes = [] } = allowedValues || {};
  const countriesAscending = countries.sort();
  const defaultValues: FormValues = editREC
    ? {
      utilityProvider: editREC.utilityProvider || "",
      utilityTypeName: editREC.utilityTypeName || "",
      country: editREC.country || "",
      renewableEnergy: editREC.renewableEnergy || "",
      startDate: editREC.startDate ? dayjs(editREC.startDate) : null,
      endDate: editREC.endDate ? dayjs(editREC.endDate) : null,
    }
    : {
      utilityProvider: recData?.providerName || "",
      utilityTypeName: recData?.utilityTypeName || "",
      country: recData?.country || "",
      renewableEnergy: "",
      startDate: null,
      endDate: null,
    };

  const { control, handleSubmit, formState: { isValid } } = useForm<FormValues>({
    mode: "onChange",
    defaultValues,
    resolver: async (data) => {
      const errors: any = {};

      // Required validations for all fields
      if (!data.utilityProvider) {
        errors.utilityProvider = { message: "Input is required" };
      }
      if (!data.utilityTypeName) {
        errors.utilityTypeName = { message: "Input is required" };
      }
      if (!data.country) {
        errors.country = { message: "Input is required" };
      }
      if (!data.renewableEnergy) {
        errors.renewableEnergy = { message: "Input is required" };
      } else {
        const renewableValue = Number(data.renewableEnergy);
        if (Number.isNaN(renewableValue)) {
          errors.renewableEnergy = { message: "Input must be a numerical value." };
        } else if (renewableValue < 0 || renewableValue > 100) {
          errors.renewableEnergy = { message: "Please enter a percentage between 0 and 100." };
        }
      }
      if (!data.startDate) {
        errors.startDate = { message: "Input is required" };
      }
      if (!data.endDate) {
        errors.endDate = { message: "Input is required" };
      }

      return {
        values: Object.keys(errors).length ? {} : data,
        errors,
      };
    },
  });

  const { mutateAsync: addRecDocument } = useMutation(addRecDataBySite);
  const { mutateAsync: editRecDocument } = useMutation(editRecDataBySite);

  const onSubmit = async (data: FormValues) => {
    const payload = {
      collectorId: recData?.id,
      utilityTypeId: utilityTypes.find(
        (ut) => ut.name.toLowerCase() === data.utilityTypeName.toLowerCase()
      )?.id || 0,
      siteId: Number(site),
      utilityProvider: data.utilityProvider,
      country: data.country,
      renewableEnergy: data.renewableEnergy,
      startDate: checkNullValue(data.startDate?.format("YYYY-MM-DDT00:00:00[Z]")),
      endDate: checkNullValue(data.endDate?.format("YYYY-MM-DDT00:00:00[Z]")),
    };

    const onSuccess = () => {
      notify.success(`RECs have been successfully ${editREC ? "edited" : "added"}!`);
      queryClient.invalidateQueries([QUERY_KEYS.SITES]);
    }

    const mutation = editREC
      ? editRecDocument({ recId: editREC.recId, payload }, { onSuccess })
      : addRecDocument(payload, { onSuccess });

    try {
      const newRecResponseData = await mutation;
      if (newRecResponseData && newRecResponseData.data?.recId) navigate(`/recDetail/${newRecResponseData.data?.recId}?s=${site}`);
      onClose();
    } catch (error: any) {
      notify.error(error?.response?.data || `Error ${editREC ? "editing" : "adding"} Utility Provider Renewable Energy Rate!`);
    }
  };

  const sortedColumns = CUSTOM_FIELDS_ORDER.map((key) =>
    columns?.find((column: any) => column.accessorKey === key)
  ).filter(Boolean);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editREC ? "Utility Provider Renewable Energy Rate Details" : "New Utility Provider Renewable Energy Rate Details"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={3}>
            {sortedColumns.map((column) => (
              <RenderField
                key={column.header}
                control={control}
                column={column}
                countries={countriesAscending}
                utilityTypes={utilityTypes}
              />
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container alignItems="center" justifyContent="flex-end" mb={2} ml={2}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isValid}
            >
              {editREC ? "Save" : "Add"}
            </Button>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};